import { DragState } from "^/useDragAndDrop";
import { forwardRef, memo, useEffect, useRef } from "react";

interface DraggableItemProps<T> {
  item: T;
  index: number;
  dragState: DragState;
  onTouchStart: (
    index: number,
    event: React.TouchEvent | React.PointerEvent,
    isHandle?: boolean
  ) => void;
  renderItem: (item: T, index: number) => React.ReactNode;
  enableDragHandle?: boolean;
}

const DraggableItemComponent = <T,>(
  {
    item,
    index,
    dragState,
    onTouchStart,
    renderItem,
    enableDragHandle = false,
  }: DraggableItemProps<T>,
  ref: React.ForwardedRef<HTMLDivElement>
) => {
  const isDragging = dragState.isDragging && dragState.draggedIndex === index;
  const itemRef = useRef<HTMLDivElement>(null);

  // 드래그 중인 아이템의 transform
  const transform = `translateY(${dragState.offsetY}px)`;

  const style: React.CSSProperties = {
    transform,
    transition: isDragging ? "none" : "transform 0.2s ease",
    opacity: isDragging ? 0.8 : 1,
    zIndex: isDragging ? 1000 : 1,
    position: "relative" as const,
    touchAction: "none" as const,
    WebkitTouchCallout: "none" as const,
    WebkitUserSelect: "none" as const,
    userSelect: "none" as const,
  };

  // 드래그 핸들 이벤트 연결
  useEffect(() => {
    if (!enableDragHandle || !itemRef.current) return;

    const dragHandle = itemRef.current.querySelector(".drag-handle");
    if (!dragHandle) return;

    const handleTouchStart = (e: TouchEvent) => {
      e.stopPropagation();
      onTouchStart(index, e as unknown as React.TouchEvent, true);
    };

    const handlePointerDown = (e: PointerEvent) => {
      e.stopPropagation();
      onTouchStart(index, e as unknown as React.PointerEvent, true);
    };

    dragHandle.addEventListener(
      "touchstart",
      handleTouchStart as EventListener
    );
    dragHandle.addEventListener(
      "pointerdown",
      handlePointerDown as EventListener
    );

    return () => {
      dragHandle.removeEventListener(
        "touchstart",
        handleTouchStart as EventListener
      );
      dragHandle.removeEventListener(
        "pointerdown",
        handlePointerDown as EventListener
      );
    };
  }, [enableDragHandle, index, onTouchStart]);

  return (
    <div
      ref={(el) => {
        if (typeof ref === "function") {
          ref(el);
        } else if (ref) {
          (ref as React.MutableRefObject<HTMLDivElement | null>).current = el;
        }
        (itemRef as any).current = el;
      }}
      style={style}
      className={`draggable-item ${
        isDragging
          ? "shadow-lg scale-105 draggable-item--dragging"
          : "draggable-item--idle"
      }`}
      onTouchStart={(e) => {
        if (enableDragHandle) {
          // 드래그 핸들에서만 시작
          return;
        }
        onTouchStart(index, e);
      }}
      onPointerDown={(e) => {
        if (enableDragHandle) {
          return;
        }
        onTouchStart(index, e);
      }}
    >
      {enableDragHandle ? (
        renderItem(item, index)
      ) : (
        <div className="flex gap-3 justify-between items-center w-full">
          {renderItem(item, index)}
        </div>
      )}
    </div>
  );
};

const DraggableItemWithRef = forwardRef(DraggableItemComponent);
DraggableItemWithRef.displayName = "DraggableItem";

export const DraggableItem = memo(DraggableItemWithRef) as <T>(
  props: DraggableItemProps<T> & { ref?: React.Ref<HTMLDivElement> }
) => React.ReactElement;
